import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/lib/auth";
import HomePage from "@/pages/Home";
import PostEventHome from "@/pages/PostEventHome";
import Highlights from "@/pages/Highlights";
import Impact from "@/pages/Impact";
import PostEventResources from "@/pages/PostEventResources";
import LoginPage from "@/pages/Login";
import VerifyPage from "@/pages/Verify";
import PaymentResult from "@/pages/PaymentResult";
import ExhibitorDashboard from "@/pages/ExhibitorDashboard";
import SchedulePage from "@/pages/Schedule";
import Directory from "@/pages/Directory";
import ExhibitorProfile from "@/pages/ExhibitorProfile";
import Toolkit from "@/pages/Toolkit";
import Procurement from "@/pages/Procurement";
import Info from "@/pages/Info";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Admin from "@/pages/Admin";
import AdminWorkspaces from "@/pages/admin/Workspaces";
import RegistrationWorkspace from "@/pages/admin/Registration";
import ProductionWorkspace from "@/pages/admin/Production";
import EventOpsWorkspace from "@/pages/admin/EventOps";
import LogisticsWorkspace from "@/pages/admin/Logistics";
import FinanceWorkspace from "@/pages/admin/Finance";
import SupplierProject from "@/pages/admin/SupplierProject";
import LaunchReadiness from "@/pages/admin/LaunchReadiness";
import AdminDocumentsEditor from "@/pages/admin/DocumentsEditor";
import AdminOperationsMatrix from "@/pages/admin/OperationsMatrix";
import MatchmakingConsole from "@/pages/admin/MatchmakingConsole";
import MatchmakingPrint from "@/pages/admin/MatchmakingPrint";
import PrintItinerary from "@/pages/PrintItinerary";
import RunSheet from "@/pages/RunSheet";
import MatchmakingRunbook from "@/pages/admin/MatchmakingRunbook";
import ProgramTracker from "@/pages/admin/ProgramTracker";
import ProgramReport from "@/pages/admin/ProgramReport";
import AdminSurvey from "@/pages/admin/AdminSurvey";
import AdminProgress from "@/pages/admin/AdminProgress";
import Survey from "@/pages/Survey";
import SupplierProfile from "@/pages/SupplierProfile";
import SBEProfile from "@/pages/SBEProfile";
import Matchmaking from "@/pages/Matchmaking";
import EventDocuments from "@/pages/EventDocuments";
import EventDocViewer from "@/pages/EventDocViewer";
import AskAI from "@/pages/AskAI";
import SupplierSuccess from "@/pages/SupplierSuccess";
import ExecutiveDashboard from "@/pages/ExecutiveDashboard";
import Operations from "@/pages/Operations";
import Resources from "@/pages/Resources";
import Leads from "@/pages/Leads";
import Venue from "@/pages/Venue";
import Presentation from "@/pages/Presentation";
import Opportunities from "@/pages/Opportunities";
import ExecutiveVision from "@/pages/ExecutiveVision";
import Onboarding from "@/pages/Onboarding";
import RegistrationComplete from "@/pages/RegistrationComplete";
import RegistrationPicker from "@/pages/RegistrationPicker";
import ParticipantRegistration from "@/pages/ParticipantRegistration";
import CheckIn from "@/pages/CheckIn";
import CommandCenter from "@/pages/CommandCenter";
import EventSettings from "@/pages/EventSettings";
import ProofReview from "@/pages/ProofReview";
import MatchmakingBrief from "@/pages/MatchmakingBrief";
import ErrorBoundary from "@/components/ErrorBoundary";
import { Terms, Privacy, DataRetention } from "@/pages/Legal";

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<PostEventHome />} />
            <Route path="/highlights" element={<Highlights />} />
            <Route path="/impact" element={<Impact />} />
            <Route path="/event-home" element={<HomePage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/verify" element={<VerifyPage />} />
            <Route path="/proofs/review/:token" element={<ProofReview />} />
            <Route path="/matchmaking/brief/:token" element={<MatchmakingBrief />} />
            <Route path="/register" element={<RegistrationPicker />} />
            <Route path="/register/sbe" element={<ParticipantRegistration type="sbe" />} />
            <Route path="/register/tl-attendee" element={<ParticipantRegistration type="tl_attendee" />} />
            <Route path="/register/guest" element={<ParticipantRegistration type="summit_guest" />} />
            {/* Speaker registration route removed from public surface until final lineup is confirmed. */}
            <Route path="/register/speaker" element={<ParticipantRegistration type="speaker" />} />
            <Route path="/register/prime" element={<Navigate to="/onboarding" replace />} />
            <Route path="/onboarding" element={<Onboarding />} />
            <Route path="/registration-complete" element={<RegistrationComplete />} />
            <Route path="/payment/success" element={<PaymentResult outcome="success" />} />
            <Route path="/payment/cancel" element={<PaymentResult outcome="cancel" />} />
            <Route path="/dashboard" element={<ExhibitorDashboard />} />
            <Route path="/schedule" element={<SchedulePage />} />
            <Route path="/breakouts" element={<SchedulePage breakoutsOnly />} />
            <Route path="/directory" element={<Directory />} />
            <Route path="/directory/:id" element={<ExhibitorProfile />} />
            <Route path="/toolkit" element={<Toolkit />} />
            <Route path="/procurement" element={<Procurement />} />
            {/* Public Speakers page is feature-flagged off until Display America confirms the final lineup. */}
            <Route path="/speakers" element={<Navigate to="/" replace />} />
            <Route path="/ask" element={<AskAI />} />
            <Route path="/resources" element={<PostEventResources />} />
            <Route path="/leads" element={<Leads />} />
            <Route path="/supplier-success" element={<SupplierSuccess />} />
            <Route path="/business-impact" element={<Navigate to="/supplier-success" replace />} />
            <Route path="/executive" element={<ExecutiveDashboard />} />
            <Route path="/operations" element={<Operations />} />
            <Route path="/venue" element={<Venue />} />
            <Route path="/opportunities" element={<Opportunities />} />
            <Route path="/opportunities/:slug" element={<Opportunities />} />
            <Route path="/demo" element={<Presentation />} />
            <Route path="/executive-vision" element={<ExecutiveVision />} />
            <Route path="/vision" element={<ExecutiveVision />} />
            <Route path="/info" element={<Info />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/data-retention" element={<DataRetention />} />
            <Route path="/admin" element={<AdminWorkspaces />} />
            <Route path="/admin/registration" element={<RegistrationWorkspace />} />
            <Route path="/admin/production" element={<ProductionWorkspace />} />
            <Route path="/admin/event-ops" element={<EventOpsWorkspace />} />
            <Route path="/admin/logistics" element={<LogisticsWorkspace />} />
            <Route path="/admin/finance" element={<FinanceWorkspace />} />
            <Route path="/admin/supplier/:id" element={<SupplierProject />} />
            <Route path="/admin/launch-readiness" element={<LaunchReadiness />} />
            <Route path="/admin/documents" element={<AdminDocumentsEditor />} />
            <Route path="/admin/operations" element={<AdminOperationsMatrix />} />
            <Route path="/admin/matchmaking" element={<MatchmakingConsole />} />
            <Route path="/admin/matchmaking/print" element={<MatchmakingPrint />} />
            <Route path="/admin/matchmaking/runbook" element={<MatchmakingRunbook />} />
            <Route path="/admin/program" element={<ProgramTracker />} />
            <Route path="/admin/program/report" element={<ProgramReport />} />
            <Route path="/admin/survey" element={<AdminSurvey />} />
            <Route path="/admin/progress" element={<AdminProgress />} />
            <Route path="/survey" element={<Survey />} />
            <Route path="/s/:token" element={<Survey />} />
            <Route path="/supplier/:id" element={<SupplierProfile />} />
            <Route path="/sbe/:id" element={<SBEProfile />} />
            <Route path="/matchmaking" element={<Matchmaking />} />
            <Route path="/matchmaking/print" element={<PrintItinerary />} />
            <Route path="/runsheet" element={<RunSheet />} />
            <Route path="/documents" element={<EventDocuments />} />
            <Route path="/documents/:slug" element={<EventDocViewer />} />
            <Route path="/admin/legacy" element={<Admin />} />
            <Route path="/checkin" element={<CheckIn />} />
            <Route path="/command-center" element={<CommandCenter />} />
            <Route path="/event-settings" element={<EventSettings />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-center" />
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
